// The task model's pure rules (#11942): what the viewer can act on, where a
// list stands, a list's order and step, and how an assignee reads. Nothing
// here holds state; every surface reads persisted lists through data.ts.
import type {
  ActedBy,
  Assignee,
  AssigneePerson,
  ListStatus,
  Task,
  TaskList,
} from "./types";
import type { ApiStepTaskDef } from "./api";
import { avatarInitials } from "@/components/FpPages/Agents/workflow/InitialsMark";

/** The members a definition's person task names, in order
 * (task-many-assignees). The server sends `owners` on every task; a task
 * the editor has only just made, before its first save, has the one
 * `owner_user_id` it was given. */
export const ownersOf = (
  task: Pick<ApiStepTaskDef, "owners" | "owner_user_id" | "owner_name">
): { userId: string; name: string }[] => {
  if (task.owners && task.owners.length > 0)
    return task.owners.map((o) => ({
      userId: o.user_id,
      name: o.name ?? o.user_id,
    }));
  return task.owner_user_id && task.owner_name
    ? [{ userId: task.owner_user_id, name: task.owner_name }]
    : [];
};

/** Today as a calendar day in the viewer's clock. */
export const today = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

/** The signed-in user is the task's person, or any one of its people
 * (task-many-assignees: any one of them may act on it). */
export const isViewer = (assignee: Assignee): boolean =>
  assignee.kind === "person" && assignee.viewer;

/** A person's task on `people`, in order, as `viewerUserId` reads it;
 * `fallbackName` names it when nobody in it is a user. */
export const personAssignee = (
  people: ReadonlyArray<{ userId: string; name: string }>,
  viewerUserId: string | null,
  fallbackName: string
): Assignee => {
  const shaped: AssigneePerson[] = people.map((p) => ({
    userId: p.userId,
    name: p.name,
    initials: avatarInitials(p.name),
    isViewer: p.userId === viewerUserId,
  }));
  const name =
    shaped.length > 0 ? joinNames(shaped.map((p) => p.name)) : fallbackName;
  return {
    kind: "person",
    name,
    initials: shaped[0]?.initials ?? avatarInitials(name),
    people: shaped,
    viewer: shaped.some((p) => p.isViewer),
  };
};

const ACTED_VERB: Record<ActedBy["outcome"], string> = {
  done: "Done",
  approved: "Approved",
  declined: "Declined",
  skipped: "Skipped",
};

/** Who settled a task, in its detail line: "Done by Ankit Anand",
 * "Approved by …", "Declined by …", "Skipped by …" (task-many-assignees:
 * any one of its people may). */
export const actedByLine = (
  actedBy: ActedBy,
  viewerUserId: string | null
): string =>
  `${ACTED_VERB[actedBy.outcome]} by ${
    viewerUserId !== null && actedBy.userId === viewerUserId
      ? "you"
      : actedBy.name
  }`;

/** Words that start a sentence or a label: "you and Ankit" reads "You and
 * Ankit" there. */
export const sentenceCase = (words: string): string =>
  words.charAt(0).toUpperCase() + words.slice(1);

/** Words that follow others in a line: "Declined by Hunter" reads
 * "declined by Hunter" after "waiting on your approval · ". */
export const midSentence = (words: string): string =>
  words.charAt(0).toLowerCase() + words.slice(1);

/** Names read together: "A", "A and B", "A, B and 2 more". */
export const joinNames = (names: ReadonlyArray<string>): string => {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
};

/** Finpilot's own actors: the agent and the system. */
export const isAgents = (t: Task): boolean =>
  t.assignee.kind === "agent" || t.assignee.kind === "system";

export const isFinished = (t: Task): boolean =>
  t.status === "done" || t.status === "skipped";

/** An open task past its due day. Overdue is derived, never stored. */
export const isOverdue = (t: Task, day: string = today()): boolean =>
  !isFinished(t) && t.due !== null && t.due.day < day;

/** Where a list stands, read off its tasks: done when every task is
 * finished, overdue when an open task is past its day, in progress else. */
export const listStatus = (
  list: TaskList,
  tasks: ReadonlyArray<Task>
): ListStatus => {
  const own = tasks.filter((t) => t.listId === list.id);
  if (own.length > 0 && own.every(isFinished)) return "done";
  return own.some((t) => isOverdue(t)) ? "overdue" : "in_progress";
};

/** A list's tasks in step order, then in their own order. */
export const orderedTasks = (
  list: TaskList,
  tasks: ReadonlyArray<Task>
): Task[] => {
  const rank = new Map(list.steps.map((s, i) => [s.id, i]));
  return [...tasks]
    .filter((t) => t.listId === list.id)
    .sort((a, b) => (rank.get(a.stepId) ?? 0) - (rank.get(b.stepId) ?? 0));
};

/** The step a run is at: the first step holding an unfinished task. */
export const currentStepIndex = (
  list: TaskList,
  tasks: ReadonlyArray<Task>
): number =>
  list.steps.findIndex((s) =>
    tasks.some(
      (t) => t.listId === list.id && t.stepId === s.id && !isFinished(t)
    )
  );

export const doneCount = (tasks: ReadonlyArray<Task>): number =>
  tasks.filter((t) => t.status === "done").length;

/** The tasks the viewer may act on. On a hand-made list, their own open
 * tasks. On a run, their own (or nobody's) unfinished tasks in any OPEN
 * step: a step is open once every step it waits on is finished, so two
 * steps open side by side are both live, and an approver behind another
 * approver still waits. A task the run has not reached says so itself
 * (`waiting`), which is what keeps a surface holding only some of the
 * list's tasks honest: a step with none of its tasks in view reads as
 * finished here, and only that status holds its dependents back. */
export const actionableTaskIds = (
  list: TaskList,
  tasks: ReadonlyArray<Task>
): Set<string> => {
  const out = new Set<string>();
  const ordered = orderedTasks(list, tasks);
  if (list.source.kind !== "run") {
    for (const t of ordered) {
      if (!isFinished(t) && isViewer(t.assignee) && t.status !== "waiting")
        out.add(t.id);
    }
    return out;
  }
  // Steps open side by side (round 14), so the viewer's task may sit in any
  // OPEN step, not only the first with work left: a step is open once every
  // step it waits on is finished (of the tasks in view).
  const finishedStep = (index: number): boolean =>
    ordered.filter((t) => t.stepId === list.steps[index]?.id).every(isFinished);
  const openSteps = new Set(
    list.steps
      .filter((step) => step.waitsOn.every((n) => finishedStep(n - 1)))
      .map((step) => step.id)
  );
  for (const t of ordered) {
    if (isFinished(t) || !openSteps.has(t.stepId)) continue;
    // A surface may hold only some of the list's tasks (a person's page
    // reads theirs); a task the run has not reached says so itself.
    if (t.status === "waiting") continue;
    // An unowned task is anyone's to claim, unless a record's attribute
    // named nobody for it: that one says why and waits on no one
    // (workflow-shape-bar, "An attribute-owned task that could not
    // resolve is nobody's").
    if (
      isViewer(t.assignee) ||
      (t.assignee.kind === "nobody" && !t.assigneeNote)
    )
      out.add(t.id);
  }
  return out;
};

/** A person's name for a line; the viewer reads as the given fallback. */
export const assigneeName = (
  assignee: Assignee,
  /** The surface's word for the viewer when they alone hold the task. */
  viewerName: string = "you"
): string => {
  switch (assignee.kind) {
    case "agent":
      return "This agent";
    case "system":
      return "System";
    case "person": {
      if (!assignee.viewer) return assignee.name;
      // The viewer alone reads by the surface's word for them.
      if (assignee.people.length <= 1) return viewerName;
      // Among several, one order everywhere, the server's, with the
      // viewer as "you" in their place (task-many-assignees); a caller
      // that starts a sentence with it capitalises (`sentenceCase`).
      return joinNames(
        assignee.people.map((p) => (p.isViewer ? "you" : p.name))
      );
    }
    case "nobody":
      return "Unassigned";
  }
};

export const assigneeInitials = (
  assignee: Assignee,
  viewerName: string
): string => {
  switch (assignee.kind) {
    case "agent":
    case "system":
      return "FP";
    case "person":
      // The viewer alone reads by the surface's name for them, as before.
      return assignee.viewer && assignee.people.length === 1
        ? avatarInitials(viewerName)
        : assignee.initials;
    case "nobody":
      return "–";
  }
};

/** A name's initials, by the one rule every avatar uses
 * (`avatarInitials`), for callers that read them from the tasks store. */
export const initialsOf = (name: string): string => avatarInitials(name);

/** The task a list opens on (#11942 round 6): the viewer's actionable
 * task first; else an agent's task under way, whose output so far is the
 * thing to watch; else nothing — someone else's open task is theirs, and
 * the reader opens it. */
export const defaultSelection = (
  list: TaskList,
  tasks: ReadonlyArray<Task>
): string | null => {
  const ordered = orderedTasks(list, tasks);
  const actionable = actionableTaskIds(list, tasks);
  const mine = ordered.find((t) => actionable.has(t.id));
  if (mine) return mine.id;
  const working = ordered.find(
    (t) => t.status === "in_progress" && isAgents(t)
  );
  return working?.id ?? null;
};
